import { defer, of as observableOf, throwError, Observable, Subject } from "rxjs";
import { catchError, finalize, map, retry, shareReplay } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Store } from "@ngrx/store";
import { CookieService } from "ngx-cookie-service";
import { getAuthUser, State } from "../reducers";
import { TOKEN_REFRESH, USER_LOGOUT } from "../reducers/actions/auth-user";
import { getApiPath, MAX_TOKEN_REFRESH_RETRIES } from "../common";
import { AuthModel } from "app/modules/user/models/auth/auth.model";
import { LanguagesConfig } from "app/configs/languages.config";

@Injectable()
export class AuthHttpService {
  /**
   * Emitted when the refresh token itself was rejected, so the session cannot be recovered.
   * AuthService listens and logs out; this service cannot depend on it the other way round.
   */
  public readonly sessionExpired = new Subject<void>();

  private loggedUser: AuthModel;
  private languagesList = LanguagesConfig.languagesList;
  private refreshRequest: Observable<string> | null = null;

  constructor(
    private http: HttpClient,
    private store: Store<State>,
    private cookieService: CookieService
  ) {
    this.store.select(getAuthUser).subscribe((user: AuthModel) => (this.loggedUser = user));
  }

  public get<T = Response>(url: string, options?, onlyAuthenticated = false): Observable<T> {
    return this.makeRequest(this.http.get.bind(this.http), url, null, options, onlyAuthenticated);
  }

  public post<T = Response>(url: string, body: any, options?, onlyAuthenticated = false): Observable<T> {
    return this.makeRequest(this.http.post.bind(this.http), url, body, options, onlyAuthenticated);
  }

  public put<T = Response>(url: string, body: any, options?, onlyAuthenticated = false): Observable<T> {
    return this.makeRequest(this.http.put.bind(this.http), url, body, options, onlyAuthenticated);
  }

  public patch<T = Response>(url: string, body: any, options?, onlyAuthenticated = false): Observable<T> {
    return this.makeRequest(this.http.patch.bind(this.http), url, body, options, onlyAuthenticated);
  }

  public remove<T = Response>(
    url: string,
    optionsBody: any = false,
    options?,
    onlyAuthenticated = false
  ): Observable<T> {
    return this.makeRequest<T>(this.http.delete.bind(this.http), url, null, options, onlyAuthenticated, optionsBody);
  }

  private makeRequest<T = Response>(
    httpFunction: (url: string, body?: any, options?) => Observable<T>,
    url: string,
    body?: any,
    options?,
    onlyAuthenticated = false,
    optionsBody = false
  ): Observable<T> {
    const isLoggedIn = this.verifyUserLoggedIn();
    if (!isLoggedIn && onlyAuthenticated) {
      return observableOf(null);
    }

    // Deferred so every attempt picks up the token that is current at that moment.
    const request = defer(() => {
      const newOptions = isLoggedIn ? this.getAuthOptions(optionsBody, options) : this.getOptions(options);

      if (body) {
        return httpFunction(url, body, newOptions);
      }
      return httpFunction(url, newOptions);
    });

    return request.pipe(
      retry({
        count: MAX_TOKEN_REFRESH_RETRIES,
        delay: (error) => this.refreshExpiredToken(error)
      })
    );
  }

  /**
   * Exchange the refresh token for a new access token so the failed request can be replayed.
   * Errors through when the failure was not an expired access token, which ends the retries.
   */
  private refreshExpiredToken(error: unknown): Observable<string> {
    if (!this.isExpiredAccessToken(error) || !this.loggedUser?.refresh) {
      return throwError(() => error);
    }

    if (!this.refreshRequest) {
      this.refreshRequest = this.requestNewAccessToken();
    }

    // Report the original 401 rather than the refresh failure when the session is really gone.
    return this.refreshRequest.pipe(catchError(() => throwError(() => error)));
  }

  /**
   * simplejwt reports a rejected access token by listing the token classes it tried.
   * The auth endpoints run no authentication, so their 401s never take this shape.
   */
  private isExpiredAccessToken(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
      return false;
    }

    // Blob and text response types leave the body unparsed, so the shape cannot be read
    // here. The auth endpoints always answer with JSON, so falling back to the bare
    // status cannot mistake rejected credentials for an expired token.
    if (!error.error || typeof error.error !== "object" || error.error instanceof Blob) {
      return true;
    }

    const messages = error.error.messages;
    return Array.isArray(messages) && messages.some((message) => message?.token_type === "access");
  }

  /**
   * Shared between all requests that failed while the same access token was expired,
   * so a burst of 401s triggers a single refresh call.
   */
  private requestNewAccessToken(): Observable<string> {
    return this.http.post(`${getApiPath()}accounts/token-refresh/`, { refresh: this.loggedUser.refresh }).pipe(
      map((response: any) => {
        const refreshedUser = AuthModel.fromResponse({ ...this.loggedUser, access: response.access });
        this.store.dispatch({ type: TOKEN_REFRESH, payload: refreshedUser });
        return refreshedUser.access;
      }),
      catchError((error: unknown) => {
        // A rejected refresh token means the session is over. Anything else (offline,
        // server error) is transient and must not throw the user out.
        if (error instanceof HttpErrorResponse && error.status === 401) {
          this.sessionExpired.next();
        }
        return throwError(() => error);
      }),
      finalize(() => (this.refreshRequest = null)),
      shareReplay(1)
    );
  }

  /** Verify is user is logged in. Expiry is left to the backend, which answers 401. */
  private verifyUserLoggedIn(): boolean {
    return Boolean(this.loggedUser?.access);
  }

  /** Return Authentication header needed for JWT Auth */
  private getAuthOptions(optionsBody, options?) {
    const newOptions = options ? Object.assign({}, options) : {};
    if (!newOptions.headers || !(newOptions.headers instanceof HttpHeaders)) {
      newOptions.headers = new HttpHeaders();
    }

    newOptions.headers = newOptions.headers.append(
      "Authorization",
      this.loggedUser ? `JWT ${this.loggedUser.access}` : ""
    );

    newOptions.headers = this.updateHeadersWithLang(newOptions.headers);

    if (optionsBody) {
      newOptions.body = optionsBody;
    }

    return newOptions;
  }

  private getOptions(options?) {
    const newOptions = options ? Object.assign({}, options) : {};

    if (!newOptions.headers) {
      newOptions.headers = new HttpHeaders();
    }

    newOptions.headers = this.updateHeadersWithLang(newOptions.headers);
    return newOptions;
  }

  private updateHeadersWithLang(headers) {
    const lang: string = localStorage.getItem("taxando-lang");
    headers = headers.append("Accept-Language", lang ? lang : this.languagesList[0].code); //set default lang as de

    return headers;
  }
}
